import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActions, 
  Button, 
  Divider, 
  Chip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  IconButton,
  useMediaQuery,
  Paper,
  useTheme
} from '@mui/material';
import { 
  Business, 
  Event, 
  Person, 
  VerifiedUser, 
  GppBad, 
  Visibility, 
  Check, 
  AccessTime, 
  Image,
  Close,
  Link,
  EditNote
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { tokens } from "../../theme";

const ProcessImprovementList = ({ processes, colors: propColors, onRefresh }) => {
  const theme = useTheme();
  const colors = propColors || tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Dialog state for image preview
  const [imageDialog, setImageDialog] = useState({
    open: false,
    imageSrc: '',
    title: ''
  });

  // Dialog state for signature preview
  const [signatureDialog, setSignatureDialog] = useState({
    open: false,
    signatureSrc: '',
    name: ''
  });

  // Handle opening image in dialog
  const handleOpenImage = (imageSrc, title) => {
    setImageDialog({
      open: true,
      imageSrc,
      title
    });
  };

  // Handle opening signature in dialog
  const handleOpenSignature = (signatureSrc, name) => {
    setSignatureDialog({
      open: true,
      signatureSrc,
      name
    });
  };

  // Close dialogs
  const handleCloseImageDialog = () => {
    setImageDialog({ ...imageDialog, open: false });
  };

  const handleCloseSignatureDialog = () => {
    setSignatureDialog({ ...signatureDialog, open: false });
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: de });
    } catch (error) {
      return 'Ungültiges Datum';
    }
  };

  // Render empty state
  if (!processes || processes.length === 0) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 5,
          borderRadius: '10px',
          backgroundColor: colors.primary[400],
          textAlign: 'center',
          maxWidth: '800px',
          margin: '0 auto'
        }}
      >
        <Image sx={{ fontSize: 60, color: colors.grey[500], mb: 2 }} />
        <Typography variant="h5" color={colors.grey[300]} fontWeight="bold" mb={1}>
          Keine Prozessverbesserungen gefunden
        </Typography>
        <Typography variant="body1" color={colors.grey[400]} mb={3}>
          Erstellen Sie eine neue Prozessverbesserung, um zu beginnen.
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 20px",
            '&:hover': {
              backgroundColor: colors.blueAccent[800],
            },
          }}
          onClick={onRefresh}
        >
          AKTUALISIEREN
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" color={colors.grey[100]} fontWeight="bold">
          Prozessverbesserungen
        </Typography>
        
        <Chip
          icon={<AccessTime />}
          label={`${processes.length} Einträge`}
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            '& .MuiChip-icon': {
              color: colors.grey[100],
            },
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {processes.map((process) => (
          <Grid item xs={12} md={6} lg={4} key={process._id}>
            <Card 
              elevation={3}
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                backgroundColor: colors.primary[400],
                borderRadius: '10px',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: `0 12px 20px -10px ${colors.primary[900]}`,
                },
              }}
            >
              {/* Card Header */}
              <Box 
                p={2} 
                display="flex" 
                alignItems="center" 
                justifyContent="space-between"
                borderBottom={`1px solid ${colors.primary[500]}`}
              >
                <Box>
                  <Typography variant="h5" color={colors.grey[100]} fontWeight="bold" gutterBottom noWrap>
                    {process.title}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Event fontSize="small" sx={{ color: colors.greenAccent[500], mr: 1 }} />
                    <Typography variant="body2" color={colors.grey[300]}>
                      {formatDate(process.createdAt)}
                    </Typography>
                    {process.relatedReklamationId && (
                      <Chip
                        icon={<Link />}
                        label="Reklamation"
                        size="small"
                        sx={{
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          '& .MuiChip-icon': {
                            color: colors.grey[100],
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Chip 
                  icon={process.lawsAcknowledgement ? <VerifiedUser /> : <GppBad />}
                  label={process.lawsAcknowledgement ? "Anforderungen bestätigt" : "Bestätigung ausstehend"}
                  sx={{
                    backgroundColor: process.lawsAcknowledgement 
                      ? colors.greenAccent[700] 
                      : colors.redAccent[700],
                    color: colors.grey[100],
                    '& .MuiChip-icon': {
                      color: colors.grey[100],
                    },
                  }}
                />
              </Box>

              {/* Image (if exists) */}
              {process.image && process.image !== 'default-process.jpg' && (
                <Box 
                  sx={{ 
                    position: 'relative',
                    paddingTop: '56.25%', // 16:9 aspect ratio
                    '&:hover .image-overlay': {
                      opacity: 1,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={process.image}
                    alt={process.title}
                    sx={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box 
                    className="image-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<Visibility />}
                      onClick={() => handleOpenImage(process.image, process.title)}
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        '&:hover': {
                          backgroundColor: colors.blueAccent[600],
                        },
                      }}
                    >
                      Bild anzeigen
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Description */}
              <CardContent sx={{ flexGrow: 1, py: 2 }}>
                <Typography
                  variant="body1"
                  color={colors.grey[200]}
                  sx={{
                    display: '-webkit-box',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    mb: 1
                  }}
                >
                  {process.description}
                </Typography>
                
                {/* Enhanced Signature Status Section */}
                <Box mt={2}>
                  <Typography variant="subtitle2" color={colors.greenAccent[400]} gutterBottom>
                    Unterschriften-Status
                  </Typography>
                  
                  {/* Original Creator Signature */}
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip
                      icon={<VerifiedUser />}
                      label="Ersteller-Unterschrift"
                      size="small"
                      onClick={() => handleOpenSignature(process.signature, 'Ersteller')}
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: colors.blueAccent[600],
                        },
                        '& .MuiChip-icon': {
                          color: colors.grey[100],
                        },
                      }}
                    />
                  </Box>

                  {/* Mobile Signatures */}
                  {process.mobileSignatures && process.mobileSignatures.length > 0 ? (
                    <>
                      <Typography variant="caption" color={colors.grey[400]} display="block" mb={1}>
                        Mobile Unterschriften ({process.mobileSignatures.length}):
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {process.mobileSignatures.map((sig, index) => (
                          <Chip
                            key={index}
                            icon={<Check />}
                            label={`${sig.employeeName} - ${new Date(sig.signedAt).toLocaleDateString('de-DE', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}`}
                            size="small"
                            onClick={() => handleOpenSignature(sig.signature, sig.employeeName)}
                            sx={{
                              backgroundColor: colors.greenAccent[700],
                              color: colors.grey[100],
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: colors.greenAccent[600],
                              },
                              '& .MuiChip-icon': {
                                color: colors.grey[100],
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        icon={<AccessTime />}
                        label="Keine mobilen Unterschriften"
                        size="small"
                        sx={{
                          backgroundColor: colors.grey[700],
                          color: colors.grey[300],
                          '& .MuiChip-icon': {
                            color: colors.grey[400],
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Signature Summary */}
                  <Box mt={2} p={1.5} bgcolor={colors.primary[500]} borderRadius={1}>
                    <Typography variant="caption" color={colors.grey[300]} display="block">
                      Unterschriften-Zusammenfassung:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <VerifiedUser fontSize="small" sx={{ color: colors.blueAccent[400] }} />
                        <Typography variant="caption" color={colors.grey[200]}>
                          1 Ersteller
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <EditNote fontSize="small" sx={{ color: colors.greenAccent[400] }} />
                        <Typography variant="caption" color={colors.grey[200]}>
                          {process.mobileSignatures ? process.mobileSignatures.length : 0} Mobile
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="caption" fontWeight="bold" color={colors.greenAccent[400]}>
                          Gesamt: {1 + (process.mobileSignatures ? process.mobileSignatures.length : 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>

              <Divider sx={{ borderColor: colors.primary[500] }} />

              {/* Signature & Actions */}
              <CardActions 
                sx={{ 
                  justifyContent: 'space-between', 
                  p: 2, 
                  backgroundColor: colors.grey[900] ,
                  borderRadius: '0 0 10px 10px',
                }}
              >
                <Box display="flex" alignItems="center">
                  <Person fontSize="small" sx={{ color: colors.greenAccent[500], mr: 1 }} />
                  <Typography variant="body2" color={colors.grey[300]}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => handleOpenSignature(process.signature, process.signature)}
                      sx={{
                        color: colors.greenAccent[400],
                        p: 0,
                        minWidth: 'auto',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        textDecoration: 'underline',
                      }}
                    >
                      Unterschrift anzeigen
                    </Button>
                  </Typography>
                </Box>

                <Box>
                  <Chip
                    icon={<Check />}
                    label="Genehmigt"
                    size="small"
                    sx={{
                      backgroundColor: colors.greenAccent[700],
                      color: colors.grey[100],
                      '& .MuiChip-icon': {
                        color: colors.grey[100],
                      },
                    }}
                  />
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Image Dialog */}
      <Dialog
        open={imageDialog.open}
        onClose={handleCloseImageDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            borderRadius: '10px',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          color: colors.grey[100],
          borderBottom: `1px solid ${colors.primary[500]}`,
        }}>
          {imageDialog.title}
          <IconButton 
            onClick={handleCloseImageDialog}
            sx={{ color: colors.grey[300] }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <img 
            src={imageDialog.imageSrc} 
            alt={imageDialog.title}
            style={{ 
              width: '100%', 
              display: 'block',
              maxHeight: '80vh',
              objectFit: 'contain'
            }} 
          />
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog
        open={signatureDialog.open}
        onClose={handleCloseSignatureDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '10px',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          color: colors.grey[100],
          borderBottom: `1px solid ${colors.primary[500]}`,
        }}>
          <Typography>
            Unterschrift von {signatureDialog.name}
          </Typography>
          <IconButton 
            onClick={handleCloseSignatureDialog}
            sx={{ color: colors.grey[300] }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          p: 3
        }}>
          {signatureDialog.signatureSrc && (
            <Box 
              sx={{ 
                borderRadius: '5px',
                padding: '10px',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
              }}
            >
              <img 
                src={signatureDialog.signatureSrc} 
                alt="Signature" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '200px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          <Typography 
            variant="body2" 
            color={colors.grey[300]} 
            sx={{ 
              mt: 2,
              fontStyle: 'italic'
            }}
          >
            Diese Unterschrift bestätigt die Kenntnisnahme und Zustimmung zur Prozessverbesserung
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.primary[500]}` }}>
          <Button 
            onClick={handleCloseSignatureDialog}
            sx={{ 
              color: colors.grey[100],
            }}
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessImprovementList;